import { cx } from 'emotion';
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import MobileToolbar from '@ezugi/bootstrap/src/components/MobileToolbar';
import GameResults from '@ezugi/bootstrap/src/features/game-results/GameResults';
import GameWidgets, { createWidget } from '@ezugi/bootstrap/src/features/widgets';
import { appActions } from '@ezugi/bootstrap/src/store/actions';
import {
  gameAreaMainClassName, gameAreaContainerClassName, gameAreaMainMobileClassName, gameUIClassName,
} from '@ezugi/bootstrap/src/views/GameArea/style.css';
import GameDialog from '@ezugi/bootstrap/src/views/GameDialog';
import GameFooter from '@ezugi/bootstrap/src/views/GameFooter';
import GameHeader from '@ezugi/bootstrap/src/views/GameHeader';
import GameLobby from '@ezugi/bootstrap/src/views/GameLobby';
import GameMenu from '@ezugi/bootstrap/src/views/GameMenu';
import GameNotifications from '@ezugi/bootstrap/src/views/GameNotifications';
import GameTools from '@ezugi/bootstrap/src/views/GameTools';
import GameVideo from '@ezugi/bootstrap/src/views/GameVideo';
import { DeviceInfo } from '@ezugi/utils';

import ODTPResultMessage from './components/GameResults/ODTPResultMessage';
import { gameResultsClassName } from './components/GameResults/ResultDisplay/style';
import GameRightSideDrawer from './components/GameRightSideDrawer';
import { isBackAndLayOnURL } from './utils';

const MobileTips = lazy(() => import('@ezugi/bootstrap/src/features/widgets/components/MobileTips'));
const MobileTipsBoundary = lazy(
  () => import('@ezugi/bootstrap/src/features/widgets/components/MobileTips/MobileTipsBoundary')
);

const GameInfo = createWidget(lazy(() => import('./components/GameInfo')), 'gameInfo');
const Statistics = createWidget(lazy(() => import('./components/Statistics')), 'stats');

const WelcomeDialog = lazy(() => import('./components/WelcomeDialog'));
const WelcomeDialogBL = lazy(() => import('./components/WelcomeDialog/BackAndLay'));
const GameBoard = lazy(() => import('./views/GameBoard'));
const GameBoardBackAndLay = lazy(() => import('./views/GameBoard/BackAndLay'));
const VideoOverlay = lazy(() => import('./components/VideoOverlay'));

const Payouts = createWidget(isBackAndLayOnURL
  ? lazy(() => import('./components/Payouts/BackAndLay'))
  : lazy(() => import('./components/Payouts')), 'payouts');

const MyBets = createWidget(isBackAndLayOnURL
  ? lazy(() => import('./components/MyBets/BackAndLay'))
  : lazy(() => import('./components/MyBets')), 'myBets');

export const GameArea = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appActions.app.start());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={gameAreaContainerClassName}>
      <Suspense fallback={null}>
        <GameHeader />
        <GameVideo components={{ VideoOverlay }} />
        <GameDialog gameDialogs={{ WelcomeDialog: isBackAndLayOnURL ? WelcomeDialogBL : WelcomeDialog }} />
        <main
          className={cx(gameAreaMainClassName, {
            [gameAreaMainMobileClassName]: DeviceInfo.isHandheld,
          })}
        >
          <GameMenu data-e2e="game-menu" />
          <GameWidgets>
            <GameInfo />
            <Statistics />
            <Payouts />
            <MyBets />
          </GameWidgets>
          <div className={gameUIClassName} data-e2e="game-ui">
            {!DeviceInfo.isHandheld && <GameRightSideDrawer />}
            {DeviceInfo.isHandheld && !isBackAndLayOnURL && <MobileToolbar />}
            {isBackAndLayOnURL ? <GameBoardBackAndLay /> : <GameBoard />}
            {!DeviceInfo.isHandheld && !isBackAndLayOnURL && (
              <GameTools
                components={{
                  ProgressBar: () => null,
                }}
              />
            )}
            <GameResults
              hideWinnerList={true}
              className={gameResultsClassName}
              components={{
                ResultMessage: ODTPResultMessage,
              }}
            />
          </div>
          <Suspense>
            <GameLobby />
          </Suspense>
          <GameNotifications />
        </main>

        {DeviceInfo.isHandheld && (
          <MobileTipsBoundary>
            <MobileTips />
          </MobileTipsBoundary>
        )}
        {!DeviceInfo.isHandheld ? <GameFooter /> : null}
      </Suspense>
    </div>
  );
};
